import React from "react";
import { decrypt } from "../../utils/other";
import { StatsigFlagProvider, tierFor } from "./providers/statsig";

// The FE spelling of the cross-repo ENVIRONMENT convention: CRA only exposes
// REACT_APP_*-prefixed vars to the browser bundle.
export const environment = () =>
  process.env.REACT_APP_ENVIRONMENT || "local";

export const flagConfig = () => ({
  provider: process.env.REACT_APP_FEATURE_FLAG_PROVIDER,
  sdkKey: process.env.REACT_APP_STATSIG_CLIENT_KEY,
  environment: environment(),
});

// Same rule as EpicenterApi/epicenter-new: the key decides. Key present →
// Statsig in every environment (the tier follows REACT_APP_ENVIRONMENT); key
// absent or the explicit REACT_APP_FEATURE_FLAG_PROVIDER=local override
// (e2e/CI) → offline local checker. An unrecognized environment also
// degrades to offline, mirroring the other repos' throw-then-degrade.
export const statsigEnabled = (config) =>
  config.provider !== "local" && !!config.sdkKey && !!tierFor(config.environment);

// Re-read per call — module-scope snapshots of the user blob are a documented incident class.
// StudentPortal's slice of the shared cross-repo identity scheme (same person,
// same userID in every product): anonymous logged out, student:<id> or
// parent:<id> logged in, disambiguated by role. saveSession (see
// utils/saveSession.js) writes the SAME id field (studentId/userId) for every
// role — student, parent, and staff-viewing-app-locally alike — so the role
// must gate which namespace the id is stamped into; a bare "has an id" check
// would send a parent session in as a student identity.
export const sessionUser = () => {
  try {
    const user = decrypt(localStorage.getItem("user"));
    const id = user?.studentId || user?.userId;
    if (!id) return { userID: "anonymous" };
    const role = user.role || "student";
    if (role === "student") {
      return { userID: `student:${id}`, custom: { role, kind: "sso" } };
    }
    if (role === "parent") {
      return { userID: `parent:${id}`, custom: { role, kind: "sso" } };
    }
    // Any other role (e.g. staff impersonating a student) isn't a namespace
    // this app targets yet — evaluate as anonymous rather than misclassify it.
    return { userID: "anonymous" };
  } catch {
    return { userID: "anonymous" };
  }
};

export const FeatureFlagProvider = ({ children }) => {
  const config = flagConfig();
  if (statsigEnabled(config)) {
    return (
      <StatsigFlagProvider
        sdkKey={config.sdkKey}
        environment={config.environment}
        user={sessionUser}
      >
        {children}
      </StatsigFlagProvider>
    );
  }
  // FeatureFlagContext's default value already serves the local checker.
  return <>{children}</>;
};

export { FeatureFlag } from "./activeFlags";
export { useFeatureFlag, useFeatureFlags, useFeatureFlagsReady } from "./context";
export { FLAGS } from "./registry";
